import { flatten, intersection } from 'lodash'
import { useMemo } from 'react'

import { SPIDER_CHART_COLORS } from '../constants/spider-chart-colors'
import { useAivyContext } from '../context'
import { useSetState } from './use-set-state'

const hashStringToIndex = (str, length) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  }
  return hash % length
}

// Deterministic color by ID; collision avoidance via shared usedIndices set
const getColorById = (id, usedIndices) => {
  const start = hashStringToIndex(String(id), SPIDER_CHART_COLORS.length)
  for (let i = 0; i < SPIDER_CHART_COLORS.length; i++) {
    const idx = (start + i) % SPIDER_CHART_COLORS.length
    if (!usedIndices.has(idx)) {
      usedIndices.add(idx)
      return SPIDER_CHART_COLORS[idx]
    }
  }
  return SPIDER_CHART_COLORS[start]
}

// Stabile Referenz fuer den Default, sonst laeuft das useMemo unten bei
// jedem Render neu.
const NO_HIDDEN_ANSWER_KEYS = []

export const useChartData = ({
  spaces,
  careers = [],
  hidden_answer_keys = NO_HIDDEN_ANSWER_KEYS
}) => {
  const { system } = useAivyContext()

  // Ausgeblendete Challenges fallen schon hier raus und nicht erst in der
  // Darstellung. Dadurch verschwinden ihre Dimensionen zugleich aus der
  // Konfiguration, aus dem Schnitt der gemeinsamen Dimensionen und aus der
  // Zaehlung fuer den Hinweis auf unvollstaendige Profile.
  const parsed_dimensions = useMemo(() => {
    const dimensions = JSON.parse(system.dimensions_pdf_reports.data)

    if (hidden_answer_keys.length === 0) return dimensions

    return Object.fromEntries(
      Object.entries(dimensions).filter(
        ([, { answer_key }]) => !hidden_answer_keys.includes(answer_key)
      )
    )
  }, [system, hidden_answer_keys])

  const usedColorIndices = new Set()
  const [state, setState] = useSetState({
    mode: 'LINEAR',
    spaces: spaces.map((s) => ({
      ...s,
      visible: true,
      ...getColorById(s.id, usedColorIndices)
    })),
    dimensions: Object.keys(parsed_dimensions).map((key) => ({
      key,
      ...parsed_dimensions[key],
      visible: true
    })),
    categories: [
      {
        // PERSONALITY
        id: 1,
        visible: true,
        ...system.categories['1'],
        active: true
      },
      {
        // SKILLS
        id: 2,
        visible: true,
        ...system.categories['2'],
        active: false
      },
      {
        // INTERESTS
        id: 3,
        visible: true,
        ...system.categories['3'],
        active: false
      },
      {
        // CULTURE
        id: 4,
        visible: true,
        ...system.categories['4'],
        active: false
      }
    ],
    careers: careers.map((c) => ({
      ...c,
      visible: true,
      ...getColorById(c.id, usedColorIndices)
    }))
  })

  const extractChartData = () => {
    const { spaces, dimensions, careers } = {
      spaces: state.spaces.filter((s) => s.visible),
      dimensions: state.dimensions.filter((d) => d.visible),
      careers: state.careers.filter((c) => c.visible)
    }

    // Early return if no data
    if (spaces.length === 0) {
      return { dimensions: [], spaces: [], careers: [] }
    }

    if (dimensions.length === 0) {
      return { dimensions: [], spaces: [], careers: [] }
    }

    const getDimensionsForAnswerKey = ({ answer_key }) => {
      return Object.values(parsed_dimensions)
        .filter((d) => d.answer_key === answer_key)
        .map((d) => d.key)
    }

    const spaces_dimensions_map = spaces.map((space) =>
      flatten(
        Object.keys(space.answers).map((answer_key) =>
          getDimensionsForAnswerKey({ answer_key })
        )
      )
    )

    const careers_dimensions_map = careers.map((career) =>
      Object.keys(JSON.parse(career.career_analyse).scores)
    )

    const dimensions_object = Object.values(dimensions).reduce(
      (previous, current) => ({ ...previous, [current.key]: current }),
      {}
    )

    // intersection
    // creates an array of unique values
    const dimensions_in_common = intersection(
      ...spaces_dimensions_map,
      ...careers_dimensions_map
    )
      .map((dimension_key) => dimensions_object[dimension_key])
      .filter((item) => !!item)

    // sort dimensions by category
    const sorted_dimensions = [
      ...dimensions_in_common.filter((d) => d.category === 4),
      ...dimensions_in_common.filter((d) => d.category === 3),
      ...dimensions_in_common.filter((d) => d.category === 1),
      ...dimensions_in_common.filter((d) => d.category === 2)
    ]

    const extractSpaceScore = (space) => {
      const { id, identification, answers, borderColor, backgroundColor } =
        space

      return {
        id,
        label: identification,
        data: sorted_dimensions.map((dimension) => {
          const { key: dimension_key, answer_key, score: score_key } = dimension
          const result =
            answers[answer_key].aivy_output.translatedzScores[score_key]

          if (dimension_key === 'DELEGATION_score_responsibility') {
            return { dimension_key, result: 100 - result }
          }

          return { dimension_key, result }
        }),
        borderWidth: 1,
        borderColor,
        backgroundColor
      }
    }

    const extractCareerScore = (career) => {
      const { id, title, career_analyse, borderColor, backgroundColor } = career
      const { scores } = JSON.parse(career_analyse)

      return {
        id,
        label: title,
        data: sorted_dimensions.map((dimension) => {
          const { key: dimension_key } = dimension
          const { min, max, optimal } = scores[dimension_key]

          return { dimension_key, min, max, optimal }
        }),
        borderWidth: 1,
        borderColor,
        backgroundColor
      }
    }

    // Detect incomplete profiles
    const allDimensionCounts = spaces_dimensions_map.map((dims, idx) => ({
      space: spaces[idx],
      dimensionCount: dims.length,
      dimensions: dims
    }))

    const maxDimensionCount = Math.max(
      ...allDimensionCounts.map((d) => d.dimensionCount)
    )

    const incompleteProfiles = allDimensionCounts
      .filter((d) => d.dimensionCount < maxDimensionCount)
      .map((d) => ({
        id: d.space.id,
        name: d.space.identification,
        completedDimensions: d.dimensionCount,
        totalDimensions: maxDimensionCount,
        missingDimensions: maxDimensionCount - d.dimensionCount
      }))

    const result = {
      dimensions: sorted_dimensions,
      spaces: spaces.map(extractSpaceScore),
      careers: careers.map(extractCareerScore),
      incompleteProfiles
    }

    return result
  }

  return {
    data: state,
    setData: setState,
    extractChartData,
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      // group hovered points per axis index so the radar tooltip shows one
      // entry per dataset with that dimension's real value — avoids the default
      // 'nearest' mode collapsing all zero-value points at the radar center
      interaction: { mode: 'index', intersect: false },
      scales: {
        r: {
          min: 0,
          max: 100,
          beginAtZero: true,
          angleLines: { display: true },
          grid: { circular: true },
          pointLabels: { font: { size: 14 } },
          ticks: { stepSize: 20, font: { size: 11 } }
        }
      },
      plugins: {
        // Disable all animations
        animation: false,
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (context) => {
              const value = context.parsed?.r ?? context.raw
              const formatted = Number(value).toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })
              return `${context.dataset.label}: ${formatted}`
            }
          }
        }
      }
    }
  }
}
